/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import appConfig from "constants/appConfig";
import { MEDIA_QUERY_FOR_PC } from "constants/mediaQueryForPc";
import { MEDIA_QUERY_FOR_SP } from "constants/mediaQueryForSp";
import React from "react";
import { EventCard } from "ui/EventCard";
import { StatusLabel } from "ui/StatusLabel";

import { CardLink } from "../CardLink";
import { NoSearchResult } from "../NoSearchResult";
import { SearchResultListCount } from "../SearchResultListCount";
import { SearchResultListWrapper } from "../SearchResultListWrapper";
import { ShowMoreButton } from "../ShowMoreButton";
import { isStatusVisible } from "./functions/isHiddenStatus";
import { useThis } from "./useThis";

import type { EventSearchResultItem } from "../../types/eventSearchResultItem";

type Props = {
  events: EventSearchResultItem[];
  isMoreLoading: boolean;
  totalCount: number;
  onClickShowMore: React.MouseEventHandler<HTMLButtonElement>;
};

export const EventSearchResultList: React.FC<Props> = ({
  events,
  isMoreLoading,
  totalCount,
  onClickShowMore,
}) => {
  const { ulAriaLabel } = useThis();

  // TODO: goods が公開されたら入り得ない条件分岐となるため除却
  //  Tab 利用側で検索結果がない場合は本 component を使わない仕様のため
  if (events.length <= 0) {
    return <NoSearchResult />;
  }

  return (
    <SearchResultListWrapper>
      <SearchResultListCount count={totalCount} />

      <ul aria-label={ulAriaLabel} css={eventListStyle}>
        {events.map((event) => {
          const { eventId, thumbnail, title, artists } =
            toPortalTopEventCardProps(event);
          const { eventStatus } = event;

          return (
            <li key={eventId}>
              <CardLink href={`/events/${eventId}`}>
                <EventCard
                  // 実装上parts.startTimeは必須になるため、partsがundefinedの場合は0を入れておく
                  eventOpenTime={event.parts?.[0]?.openTime ?? 0}
                  imagePath={thumbnail}
                  labelComponent={
                    isStatusVisible(eventStatus) ? (
                      <StatusLabel status={eventStatus} />
                    ) : null
                  }
                  artists={artists}
                  title={title}
                />
              </CardLink>
            </li>
          );
        })}
      </ul>

      <ShowMoreButton
        isLoading={isMoreLoading}
        isShown={events.length < totalCount}
        onClickShowMore={onClickShowMore}
      />
    </SearchResultListWrapper>
  );
};

const eventListStyle = css`
  display: grid;

  ${MEDIA_QUERY_FOR_PC} {
    gap: 16px;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 40px;
  }

  ${MEDIA_QUERY_FOR_SP} {
    gap: 8px;
    grid-template-columns: 1fr 1fr;
    margin-top: 10px;
  }
`;

type PortalTopEventCardProps = {
  eventId: string;
  thumbnail: string | null;
  title: string;
  artists: string[];
};

const toPortalTopEventCardProps = (
  item: EventSearchResultItem
): PortalTopEventCardProps => {
  return {
    eventId: item.id,
    thumbnail: getThumbnail(item.eventBanner, item.defaultImage),
    title: item.title,
    artists: item.artists,
  };
};

const getThumbnail = (
  bannerImage: string,
  defaultImage: string
): string | null => {
  const PUBLIC_DB_URL = appConfig.publicStorageDomain ?? "";
  if (bannerImage) {
    return `${PUBLIC_DB_URL}/${bannerImage}_1280x720`;
  }
  return defaultImage === "" ? null : defaultImage;
};
